import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddMailPermissionMutation as AddMailPermission } from '__generated__/graphql';

import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddMailPermissionMutation from './AddMailPermissionMutation';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

const addMailPermissionSchema = yup.object().shape({
  title: yup.string().ensure().required(),
  label: yup.string().ensure().required(),
});

export interface AddMailPermissionFormProps {
  onSuccess?: (mailPermission: AddMailPermission['addMailPermission']) => void;
  onCancel?: () => void;
}

const AddMailPermissionForm = ({
  onSuccess, onCancel,
}: AddMailPermissionFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
      label: '',
    },
  });

  const [addMailPermission, { loading, error }] = useMutation(
    AddMailPermissionMutation,
    {
      variables: {
        input: {
          project: { id: project.id },
          ...form.values,
        },
      },
      onCompleted: ({ addMailPermission: mailPermission }) => {
        onSuccess?.(mailPermission);
        notifier.success(t('notifications.mail_permission_added'));
      },
      onError: () => {
        //
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addMailPermissionSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addMailPermission()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_mail_permission')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_mail_permission_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="MailPermission_Title">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('mail_permission_title_description')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={50}
            id="MailPermission_Title"
            placeholder={t('mail_permission_title_placeholder')}
            autoFocus
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.label, touched: form.touched.label }}>
          <UI.InputLabel htmlFor="MailPermission_Label">
            {t('mail_permission_label')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('mail_permission_label_description')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('label')}
            maxLength={255}
            id="MailPermission_Label"
            placeholder={t('mail_permission_label_placeholder')}
          />
          <UI.ErrorMessages attribute={t('label')} errors={errors.label} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_mail_permission')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddMailPermissionForm;
